import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DPCLoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dpc/login`, form);
      localStorage.setItem('dpc_token', res.data.token);
      localStorage.setItem('dpc_user', JSON.stringify({
        first_name: res.data.user.first_name,
        last_name: res.data.user.last_name,
        practice_name: res.data.user.practice_name,
        email: res.data.user.email,
        subscription_status: res.data.user.subscription_status,
        trial_ends_at: res.data.user.trial_ends_at,
      }));
      navigate('/dpc/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors';

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-2/5 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/for-physicians">
          <span className="text-white font-semibold text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            Meridian for Physicians
          </span>
        </Link>
        <div>
          <h2
            className="text-3xl font-medium text-white mb-4 leading-snug"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Setting Course for Smarter Care.
          </h2>
          <p className="text-white/55 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Search cash prices across 600,000+ hospital price records and make confident referrals for your patients.
          </p>
        </div>
        <p className="text-white/30 text-xs">For informational use only. Not medical advice.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-2">Physician Portal</p>
            <h1
              className="text-3xl font-medium text-stone-900 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Welcome back
            </h1>
            <p className="text-stone-500 text-sm">Sign in to your Meridian physician account.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="dpc-login-form">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@practice.com"
                className={inputClass}
                required
                data-testid="dpc-login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className={`${inputClass} pr-12`}
                  required
                  data-testid="dpc-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="dpc-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="dpc-login-submit"
            >
              {loading ? 'Signing in…' : 'Sign In'}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-stone-500 text-center">
            Don't have an account?{' '}
            <Link to="/dpc/register" className="text-[#0D3D4A] font-medium hover:underline">
              Start free trial →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
